/**
 * A presentation clicker (Logitech R400/Spotlight, Kensington, Targus, ...)
 * is a keyboard with two useful keys. These rules turn its presses into
 * script moves, so a trainer in front of the projector can walk the agenda
 * with nothing else in hand.
 */

export type ClickerCommand = 'next' | 'prev'

/** What the agenda bar's own buttons do: Start script, Yes confirm, the arrows. */
export type ScriptMove = 'start' | 'confirm' | 'next' | 'prev'

export type KeyPress = Pick<
	KeyboardEvent,
	'key' | 'repeat' | 'ctrlKey' | 'altKey' | 'metaKey' | 'shiftKey'
> & { target: EventTarget | null }

/** A field being typed in: no key there is a command. */
export const isEditableTarget = (target: EventTarget | null): boolean =>
	target instanceof Element &&
	(['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
		target.closest('[contenteditable=""], [contenteditable="true"]') !== null)

/**
 * The command a key press means, if any.
 *
 * Page Down / Page Up are what nearly every clicker sends, and mean the same
 * wherever the focus is - except in a field being typed in. Some clickers
 * send the arrow keys instead; those count only while nothing on the page has
 * the focus, because the whiteboard moves shapes with them and fields move
 * the caret. Space and Enter would press whatever button has the focus, and a
 * held key or one with a modifier is somebody using the keyboard, not a
 * clicker.
 */
export const clickerCommand = (
	press: KeyPress,
	{ pageFocused }: { pageFocused: boolean }
): ClickerCommand | undefined => {
	if (
		press.repeat ||
		press.ctrlKey ||
		press.altKey ||
		press.metaKey ||
		press.shiftKey ||
		isEditableTarget(press.target)
	) {
		return undefined
	}
	switch (press.key) {
		case 'PageDown':
			return 'next'
		case 'PageUp':
			return 'prev'
		case 'ArrowRight':
			return pageFocused ? 'next' : undefined
		case 'ArrowLeft':
			return pageFocused ? 'prev' : undefined
		default:
			return undefined
	}
}

/** Where the script is, as far as the clicker is concerned. */
export type ScriptPosition = {
	/** The script has a current step. */
	started: boolean
	/** The chunk has items, so the script can be started. */
	canStart: boolean
	/** The current step waits for "Yes, confirm" (group work, a break). */
	needsConfirmation: boolean
	hasNext: boolean
	hasPrev: boolean
}

/**
 * The move a command makes from here. Next does whatever stands between the
 * trainer and the next step - starting the script, confirming the current
 * step - so a trainer with only a clicker is never stuck.
 */
export const scriptMoveFor = (
	command: ClickerCommand,
	at: ScriptPosition
): ScriptMove | undefined => {
	if (command === 'prev') {
		return at.started && at.hasPrev ? 'prev' : undefined
	}
	if (!at.started) {
		return at.canStart ? 'start' : undefined
	}
	if (at.needsConfirmation) {
		return 'confirm'
	}
	return at.hasNext ? 'next' : undefined
}
